import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于被窝影院',
    description: '被窝影院是一个专业的在线电影平台，致力于为影迷提供最新、最全、最高质量的电影资源。',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '被窝影院',
    url: 'https://beiwocinema.com',
    description: '被窝影院电影大全免费看，最新电影免费看和高分电影在线，电影大全无广告。',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-0000',
      contactType: 'customer service',
      email: 'contact@beiwocinema.com',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '北京市',
      addressCountry: 'CN',
      streetAddress: '朝阳区影视产业园',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '被窝影院需要注册才能看电影吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要。被窝影院所有电影完全免费，无需注册登录即可直接观看，没有任何隐藏收费。',
        },
      },
      {
        '@type': 'Question',
        name: '被窝影院有广告吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '没有。被窝影院承诺全程无广告，为您提供纯净的观影体验，绝不中途插播任何广告。',
        },
      },
      {
        '@type': 'Question',
        name: '被窝影院的电影清晰度如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '被窝影院提供高清甚至超清画质的电影资源，支持流畅播放不卡顿，让您享受影院级别的观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '被窝影院支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '被窝影院支持电脑、平板、手机等多种设备，只要连接网络即可随时随地畅享电影盛宴。',
        },
      },
      {
        '@type': 'Question',
        name: '被窝影院的电影会定期更新吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '会的。被窝影院团队24小时不间断更新片源，确保您能第一时间看到最新上映的电影和经典佳作。',
        },
      },
    ],
  };

  const testimonials = [
    {
      name: '张先生',
      role: '资深影迷',
      content: '被窝影院真的是我见过最好的免费电影网站，片源全、更新快、不卡顿，而且真的没有广告！每天睡前必刷一部。',
      rating: 5,
    },
    {
      name: '李女士',
      role: '电影爱好者',
      content: '自从发现了被窝影院，就再也没去过其他电影网站。画质清晰，分类齐全，想看的电影基本都能找到。',
      rating: 5,
    },
    {
      name: '王同学',
      role: '大学生',
      content: '宿舍里大家都用被窝影院，周末一起看电影特别方便。免费还没有广告，对学生党太友好了。',
      rating: 5,
    },
    {
      name: '赵先生',
      role: 'IT工程师',
      content: '作为技术控，我很欣赏被窝影院的加载速度和播放体验。不缓冲是真的不缓冲，技术做得很好。',
      rating: 5,
    },
    {
      name: '刘女士',
      role: '全职妈妈',
      content: '带孩子间隙刷一部电影放松，被窝影院的分类做得很好，很容易找到想看的类型。推荐给所有妈妈们。',
      rating: 5,
    },
    {
      name: '陈先生',
      role: '退休教师',
      content: '年纪大了不怎么会用复杂的软件，被窝影院界面简洁好操作，经典老片也很多，特别喜欢。',
      rating: 5,
    },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setContactForm({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('sending');
    setFormError('');
    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9nia70bdkpk99j7gjug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setContactForm({ name: '', email: '', phone: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            <div className="w-full px-4 md:px-8">
              {/* Brand Introduction */}
              <section className="mb-16 md:mb-20">
                <div className="text-center mb-10">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-accent-100 rounded-full">
                    <i className="ri-movie-2-fill text-accent-500 text-2xl"></i>
                  </div>
                  <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                    关于被窝影院
                  </h1>
                  <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                    被窝影院成立于2020年，是一个专注于为影迷提供免费高清电影在线观看的平台。我们的使命是让每一个人都能在被窝里享受电影的魅力。
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-6 rounded-lg bg-background-50 border border-background-200/60 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-full">
                      <i className="ri-eye-line text-primary-500 text-xl"></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">我们的愿景</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      打造中国最受欢迎的免费电影平台，让优质影视内容触手可及，让观影成为一种简单纯粹的享受。
                    </p>
                  </div>
                  <div className="p-6 rounded-lg bg-background-50 border border-background-200/60 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-accent-100 rounded-full">
                      <i className="ri-shield-check-line text-accent-500 text-xl"></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">品质承诺</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      我们承诺全程无广告、不缓冲、高清画质。每一部电影都经过精心筛选和优化，确保最佳观影体验。
                    </p>
                  </div>
                  <div className="p-6 rounded-lg bg-background-50 border border-background-200/60 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-secondary-100 rounded-full">
                      <i className="ri-user-heart-line text-secondary-500 text-xl"></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">用户至上</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      我们始终以用户需求为核心，持续优化产品体验。您的每一次观影反馈，都是我们前进的动力。
                    </p>
                  </div>
                </div>
              </section>

              {/* Stats */}
              <section className="mb-16 md:mb-20">
                <div className="bg-background-900 rounded-2xl p-8 md:p-12">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                    <div className="text-center">
                      <p className="text-3xl md:text-4xl font-bold text-accent-400">50,000+</p>
                      <p className="text-sm text-foreground-400 mt-2">收录电影</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl md:text-4xl font-bold text-accent-400">1,200万+</p>
                      <p className="text-sm text-foreground-400 mt-2">活跃用户</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl md:text-4xl font-bold text-accent-400">99.9%</p>
                      <p className="text-sm text-foreground-400 mt-2">播放成功率</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl md:text-4xl font-bold text-accent-400">8,000+</p>
                      <p className="text-sm text-foreground-400 mt-2">日均更新</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Reputation */}
              <section className="mb-16 md:mb-20">
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    用户口碑
                  </h2>
                  <p className="text-sm text-foreground-500">来自真实用户的评价</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {testimonials.map((t, i) => (
                    <div key={i} className="p-5 rounded-lg bg-background-50 border border-background-200/60">
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: t.rating }).map((_, j) => (
                          <i key={j} className="ri-star-fill text-accent-400 text-sm"></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-4">{t.content}</p>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-600 text-sm font-semibold">{t.name[0]}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                          <p className="text-xs text-foreground-500">{t.role}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Contact Form */}
              <section className="mb-16 md:mb-20">
                <div className="max-w-2xl mx-auto">
                  <div className="text-center mb-10">
                    <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                      联系我们
                    </h2>
                    <p className="text-sm text-foreground-500">有任何问题或建议？欢迎给我们留言</p>
                  </div>

                  <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={contactForm.name}
                          onChange={e => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-800 text-sm outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={contactForm.email}
                          onChange={e => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-800 text-sm outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">电话</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={contactForm.phone}
                        onChange={e => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-800 text-sm outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的电话（选填）"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        value={contactForm.message}
                        onChange={e => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                        rows={5}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-800 text-sm outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1 text-right">{contactForm.message.length}/500</p>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    {formError && (
                      <div className="p-3 rounded-lg bg-accent-100/50 text-accent-700 text-sm">
                        {formError}
                      </div>
                    )}
                    {formStatus === 'success' && (
                      <div className="p-3 rounded-lg bg-primary-100 text-primary-700 text-sm">
                        提交成功！感谢您的留言，我们会尽快回复。
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={formStatus === 'sending'}
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white rounded-lg font-medium text-sm transition-all whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'sending' ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>提交中...
                        </span>
                      ) : '提交留言'}
                    </button>
                  </form>
                </div>
              </section>

              {/* FAQ */}
              <section className="mb-16 md:mb-20">
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    常见问题
                  </h2>
                </div>
                <div className="max-w-3xl mx-auto space-y-3">
                  {faqSchema.mainEntity.map((faq, i) => (
                    <details key={i} className="group bg-background-50 border border-background-200/60 rounded-lg overflow-hidden cursor-pointer">
                      <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-800 list-none">
                        {faq.name}
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </summary>
                      <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {faq.acceptedAnswer.text}
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}